import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { checkPermission, checkBulkPermissions } from '../utils/checkPermission';


// Create the context
const PermissionsContext = createContext();
const normalizePathname = (pathname) => pathname.replace(/\/+$/, "") || "/";

// Custom hook to use the permissions context
export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

// Provider component
export const PermissionsProvider = ({ children }) => {
  const { pathname } = useLocation();
  const normalizedPathname = normalizePathname(pathname);
  const isCandidateRoute =
    normalizedPathname === "/careers" ||
    normalizedPathname.startsWith("/careers/");
  const isPublicEntryRoute = ['/', '/consultant'].includes(normalizedPathname);

  const [permissions, setPermissions] = useState({
    // Initialize all permissions to false
    canViewBulkUpload: false,
    canViewFormBuilder: false,
    canViewForm: false,
    canViewTrf: false,
    canViewAssignTrf: false,
    canViewVersant: false,
    canViewTinReport: false,
    canViewDocuments: false,
    canViewCreateDocument: false,
    canViewInterviewDashboard: false,
    canViewAdminDashboard: false,
    canViewSiteHRAudit: false,
    canViewCentralHRAudit: false,
    canViewCompetencySetup: false,
    canViewInterviewStatus: false,
    canViewClientMapping: false,
    canViewConsultantMapping: false,
    canViewCallStatus: false,
    canViewApplicationReport: false,
    canViewAuditReport: false,
    canViewInterviewLogs: false,
    canViewOtpLogs: false,
    canViewFeedbackReport: false,
    canViewLoginReport: false,
    canViewDistributedTrf: false,
    canViewDocumentReport: false,
    canViewAiReport: false,
    canManageUsers: false,
    canViewRegister: false,
    canViewLogs: false,
    canViewApiLogs: false,
    canViewServiceSettings: false,
    canViewSettings: false,
    canViewGenerateExcel: false,
    canFullAccessCandidate: false,
    canViewAdmin: false,
    canViewTinAccess: false,
    canViewVoiceBot: false,
    canViewQr: false,
    canViewAiService: false,
    canViewAmeyoService: false,
    canViewManagerDashboard: false,
    canViewRuleEngine: false,
    canViewAmeyoConfiguration: false,
  });

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState(null);

  const [userRole, setUserRole] = useState(localStorage.getItem('user.role') || "");

  useEffect(() => {
    if (isPublicEntryRoute || isCandidateRoute) return;

    if (userRole) {
      checkAllPermissions();
    }
  }, [userRole, isPublicEntryRoute, isCandidateRoute]);

  const checkAllPermissions = async (role = null) => {
    // If a new role is passed (e.g. from login), update the state
    if (role && role !== userRole) {
      setUserRole(role);
    }

    const roleToUse = role || userRole;

    // Only check if we have a role and not currently loading
    if (!roleToUse) {
      console.log('⏳ No user role, skipping permission check');
      return;
    }

    // If already loading, don't start another check
    if (loading) {
      console.log('⏳ Permission check already in progress');
      return;
    }

    setLoading(true);
    setError(null);

    console.log('🔄 Loading permissions from context for role:', roleToUse);

    try {
      // Prepare all checks
      const checks = [
        { entityType: "bulk_upload_module", permission: "view_bulk_upload" },
        { entityType: "formbuilder_module", permission: "view_form_builder" },
        { entityType: "forms_module", permission: "view_form" },
        { entityType: "trf_module", permission: "view_trf" },
        { entityType: "assign_trf_module", permission: "view_assign_trf" },
        { entityType: "process_based_versant_module", permission: "view_versant" },
        { entityType: "tin_usage_report_module", permission: "view_tin_report" },
        { entityType: "documents_module", permission: "view_documents" },
        { entityType: "create_document_module", permission: "view" },
        { entityType: "site_hr_audit_document_module", permission: "view_site_hr_audit_document" },
        { entityType: "central_hr_audit_document_module", permission: "view_central_hr_audit_document" },
        { entityType: "analytics_dashboard_module", permission: "view_interview_dashboard" },
        { entityType: "analytics_dashboard_module", permission: "view_admin_dashboard" },
        { entityType: "competency_setup_module", permission: "view_competency_setup" },
        { entityType: "interview_status_module", permission: "view_interview_status" },
        { entityType: "client_mapping_module", permission: "view_client_mapping" },
        { entityType: "consultant_mapping_module", permission: "view_consultant_mapping" },
        { entityType: "call_status_module", permission: "view_call_status" },
        { entityType: "application_report_module", permission: "view_application_report" },
        { entityType: "audit_report_module", permission: "view_audit_report" },
        { entityType: "interview_logs_module", permission: "view_interview_logs" },
        { entityType: "otp_logs_module", permission: "view_otp_logs" },
        { entityType: "feedback_report_module", permission: "view_feedback_report" },
        { entityType: "login_report_module", permission: "view_login_report" },
        { entityType: "distributed_trf_report_module", permission: "view_distributed_trf" },
        { entityType: "document_report_module", permission: "view_document_report" },
        { entityType: "ai_report_module", permission: "view_ai_report" },
        { entityType: "manage_users_module", permission: "view_users" },
        { entityType: "register_user_module", permission: "view_register" },
        { entityType: "logs_module", permission: "view_logs" },
        { entityType: "api_logs_module", permission: "view_api_logs" },
        { entityType: "service_setting_module", permission: "view_service_settings" },
        { entityType: "settings_module", permission: "view_settings" },
        { entityType: "generate_excel_module", permission: "view_generate_excel" },
        { entityType: "candidate_portal", permission: "full_access" },
        { entityType: "admin_module", permission: "view_admin" },
        { entityType: "tin_access_manangement_module", permission: "view_tin_report" },
        { entityType: "voice_bot_upload_module", permission: "view_voice_bot_upload" },
        { entityType: "generate_qr_code_module", permission: "view_qr_code" },
        { entityType: "ai_service_module", permission: "view_ai_service" },
        { entityType: "ameyo_service_module", permission: "view_ameyo_service" },
        { entityType: "manager_dashboard_module", permission: "view_permission" },
        { entityType: "rule_engine_module", permission: "view_rule" },
        { entityType: "ameyo_configuration_module", permission: "view_ameyo_configuration" },
      ];

      const results = await checkBulkPermissions(roleToUse, checks);

      // Helper to find a specific permission result
      const isAllowed = (entity, perm) => {
        const item = results.find(r => r.entityType === entity && r.permission === perm);
        return item ? item.allowed : false;
      };

      // Update all permissions in a single state update
      const newPermissions = {
        canViewBulkUpload: isAllowed("bulk_upload_module", "view_bulk_upload"),
        canViewFormBuilder: isAllowed("formbuilder_module", "view_form_builder"),
        canViewForm: isAllowed("forms_module", "view_form"),
        canViewTrf: isAllowed("trf_module", "view_trf"),
        canViewAssignTrf: isAllowed("assign_trf_module", "view_assign_trf"),
        canViewVersant: isAllowed("process_based_versant_module", "view_versant"),
        canViewTinReport: isAllowed("tin_usage_report_module", "view_tin_report"),
        canViewDocuments: isAllowed("documents_module", "view_documents"),
        canViewCreateDocument: isAllowed("create_document_module", "view"),
        canViewSiteHRAudit: isAllowed("site_hr_audit_document_module", "view_site_hr_audit_document"),
        canViewCentralHRAudit: isAllowed("central_hr_audit_document_module", "view_central_hr_audit_document"),
        canViewInterviewDashboard: isAllowed("analytics_dashboard_module", "view_interview_dashboard"),
        canViewAdminDashboard: isAllowed("analytics_dashboard_module", "view_admin_dashboard"),
        canViewCompetencySetup: isAllowed("competency_setup_module", "view_competency_setup"),
        canViewInterviewStatus: isAllowed("interview_status_module", "view_interview_status"),
        canViewClientMapping: isAllowed("client_mapping_module", "view_client_mapping"),
        canViewConsultantMapping: isAllowed("consultant_mapping_module", "view_consultant_mapping"),
        canViewCallStatus: isAllowed("call_status_module", "view_call_status"),
        canViewApplicationReport: isAllowed("application_report_module", "view_application_report"),
        canViewAuditReport: isAllowed("audit_report_module", "view_audit_report"),
        canViewInterviewLogs: isAllowed("interview_logs_module", "view_interview_logs"),
        canViewOtpLogs: isAllowed("otp_logs_module", "view_otp_logs"),
        canViewFeedbackReport: isAllowed("feedback_report_module", "view_feedback_report"),
        canViewLoginReport: isAllowed("login_report_module", "view_login_report"),
        canViewDistributedTrf: isAllowed("distributed_trf_report_module", "view_distributed_trf"),
        canViewDocumentReport: isAllowed("document_report_module", "view_document_report"),
        canViewAiReport: isAllowed("ai_report_module", "view_ai_report"),
        canManageUsers: isAllowed("manage_users_module", "view_users"),
        canViewRegister: isAllowed("register_user_module", "view_register"),
        canViewLogs: isAllowed("logs_module", "view_logs"),
        canViewApiLogs: isAllowed("api_logs_module", "view_api_logs"),
        canViewServiceSettings: isAllowed("service_setting_module", "view_service_settings"),
        canViewSettings: isAllowed("settings_module", "view_settings"),
        canViewGenerateExcel: isAllowed("generate_excel_module", "view_generate_excel"),
        canFullAccessCandidate: isAllowed("candidate_portal", "full_access"),
        canViewAdmin: isAllowed("admin_module", "view_admin"),
        canViewTinAccess: isAllowed("tin_access_manangement_module", "view_tin_report"),
        canViewVoiceBot: isAllowed("voice_bot_upload_module", "view_voice_bot_upload"),
        canViewQr: isAllowed("generate_qr_code_module", "view_qr_code"),
        canViewAiService: isAllowed("ai_service_module", "view_ai_service"),
        canViewAmeyoService: isAllowed("ameyo_service_module", "view_ameyo_service"),
        canViewManagerDashboard: isAllowed("manager_dashboard_module", "view_permission"),
        canViewRuleEngine: isAllowed("rule_engine_module", "view_rule"),
        canViewAmeyoConfiguration: isAllowed("ameyo_configuration_module", "view_ameyo_configuration")
      };

      setPermissions(newPermissions);


      console.log('✅ Permissions loaded successfully from context');

    } catch (error) {
      console.error("❌ Permission check failed in context:", error);
      setError(error.message);

      // Reset all permissions to false on error
      setPermissions({
        canViewBulkUpload: false,
        canViewFormBuilder: false,
        canViewForm: false,
        canViewTrf: false,
        canViewAssignTrf: false,
        canViewVersant: false,
        canViewTinReport: false,
        canViewDocuments: false,
        canViewCreateDocument: false,
        canViewInterviewDashboard: false,
        canViewAdminDashboard: false,
        canViewSiteHRAudit: false,
        canViewCentralHRAudit: false,
        canViewCompetencySetup: false,
        canViewInterviewStatus: false,
        canViewClientMapping: false,
        canViewConsultantMapping: false,
        canViewCallStatus: false,
        canViewApplicationReport: false,
        canViewAuditReport: false,
        canViewInterviewLogs: false,
        canViewOtpLogs: false,
        canViewFeedbackReport: false,
        canViewLoginReport: false,
        canViewDistributedTrf: false,
        canViewDocumentReport: false,
        canViewAiReport: false,
        canManageUsers: false,
        canViewRegister: false,
        canViewLogs: false,
        canViewApiLogs: false,
        canViewServiceSettings: false,
        canViewSettings: false,
        canViewGenerateExcel: false,
        canFullAccessCandidate: false,
        canViewAdmin: false,
        canViewTinAccess: false,
        canViewVoiceBot: false,
        canViewQr: false,
        canViewAiService: false,
        canViewAmeyoService: false,
        canViewManagerDashboard: false,
        canViewRuleEngine: false,
        canViewAmeyoConfiguration: false
      });
    } finally {
      setLoading(false);
    }
  };


  // Function to refresh permissions (useful for when user role changes)
  const refreshPermissions = () => {

    setError(null);
    checkAllPermissions();
  };

  // Function to reset permissions (useful for logout)
  const resetPermissions = () => {
    setPermissions({
      canViewBulkUpload: false,
      canViewFormBuilder: false,
      canViewForm: false,
      canViewTrf: false,
      canViewAssignTrf: false,
      canViewVersant: false,
      canViewTinReport: false,
      canViewDocuments: false,
      canViewCreateDocument: false,
      canViewInterviewDashboard: false,
      canViewAdminDashboard: false,
      canViewSiteHRAudit: false,
      canViewCentralHRAudit: false,
      canViewCompetencySetup: false,
      canViewInterviewStatus: false,
      canViewClientMapping: false,
      canViewConsultantMapping: false,
      canViewCallStatus: false,
      canViewApplicationReport: false,
      canViewAuditReport: false,
      canViewInterviewLogs: false,
      canViewOtpLogs: false,
      canViewFeedbackReport: false,
      canViewLoginReport: false,
      canViewDistributedTrf: false,
      canViewDocumentReport: false,
      canViewAiReport: false,
      canManageUsers: false,
      canViewRegister: false,
      canViewLogs: false,
      canViewApiLogs: false,
      canViewServiceSettings: false,
      canViewSettings: false,
      canViewGenerateExcel: false,
      canFullAccessCandidate: false,
      canViewAdmin: false,
      canViewTinAccess: false,
      canViewVoiceBot: false,
      canViewQr: false,
      canViewAiService: false,
      canViewAmeyoService: false,
      canViewManagerDashboard: false,
      canViewRuleEngine: false,
      canViewAmeyoConfiguration: false
    });

    setUserRole("");
    setError(null);
  };

  // Context value
  const value = {
    permissions,
    checkAllPermissions,
    loading,
    error,
    refreshPermissions,
    resetPermissions,
    userRole
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};
